const $ = require('jQuery');
const widgets = require('../widgets');
const view_widgets = require('../view_widgets');
const {formatAxisTuningProfile, formatAxisType, isAxisTypeDeprecated} = require('../../helpers');
const {ReactiveMLComponent, selectComponent, selectOption, configCardComponent} = require('./config_components/base_components');
const {configCheckerResultStates} = require('../../machinelogic');
const {
  controlDeviceComponent,
  pushComponent,
  pullComponent,
  vfdForwardReverseComponent,
  vfdOnOffComponent
} = require('./config_components/config_pneumatic_components');

import { CYPRESS_DATA_ATTRIBUTE_NAME, CypressDataAttributes } from "../../cypressDataAttributes";
// @ts-ignore
require('./axis_config_card.css');


/**
 * @typedef {Object} SingleDriveComponentState
 * @prop {*} [config] Config the axis belongs to. (optional)
 * @prop {*} [axis] The axis object (optional)
 *
 * @typedef {{drive: string} & SingleDriveComponentState} MultiDriveComponentState
 * @typedef {SingleDriveComponentState | MultiDriveComponentState} AxisComponentState
 *
 * @typedef {Object} ReactiveAxisWidgetComponentParams
 * @prop {(state: AxisComponentState, render: Function?) => JQuery<HTMLElement> | JQuery<HTMLElement>[] | []} createElement
 * @prop {JQuery<HTMLElement>} container
 * @prop {AxisComponentState} state
 * @prop {string[]} [configListenerNames]
 * @prop {string[]} [axisListenerNames]
 * @prop {string[]} [configManagerListenerNames]
 */
class ReactiveAxisWidgetComponent extends ReactiveMLComponent {

  /**
   * @param {ReactiveAxisWidgetComponentParams} param
   */
  constructor({ createElement, container, state, configListenerNames, axisListenerNames, configManagerListenerNames }) {
    super({ createElement, container, state });

    const { config, axis } = state;
    if (!config || !axis) {
      console.error("ReaxtiveAxisWidgetComponent was created with missing config or axis in its state. This will cause rendering issues.");
    }
    this.mConfig = config;
    this.mAxis = axis;
    this.mConfigListeners = {};
    this.mAxisListeners = {};
    this.mConfigManagerListeners = {};

    if (configManagerListenerNames) {
      configManagerListenerNames.forEach((name) => {
        this.mConfigManagerListeners[name] = this.render;
      });
      this.mConfig.getConfigManager().addListener(this.mConfigManagerListeners);
    }

    if (configListenerNames) {
      this.mConfigListeners = {};
      configListenerNames.forEach((name) => {
        this.mConfigListeners[name] = this.render;
      });
      this.mConfig.addListener(this.mConfigListeners);
    }
    if (axisListenerNames) {
      this.mAxisListeners = {};
      axisListenerNames.forEach((name) => {
        this.mAxisListeners[name] = this.render;
      });
      this.mAxis.addListener(this.mAxisListeners);
    }
  }

  cleanUp = () => {
    if (this.mConfigListeners) {
      this.mConfig.removeListener(this.mConfigListeners);
    }
    if (this.mAxisListeners) {
      this.mAxis.removeListener(this.mAxisListeners);
    }
    if(this.mConfigManagerListeners) {
      this.mConfig.getConfigManager().removeListener(this.mConfigManagerListeners);
    }
  };

}

/**
 *
 * @param {*} pConfig config
 * @param {string} pParameterName the name of the parameter you want to extract the list from
 * @returns {array}
 */
function getAxisConfigOptions(pConfig, pParameterName){
  const lParameterInfos = pConfig.mLibrary.getInstructionSet().config.axis.parameters.find(function(pParam) {
    return pParam.name == pParameterName;
  });
  if(lParameterInfos && lParameterInfos.values && Array.isArray(lParameterInfos.values)) { return lParameterInfos.values; }
  else { return []; }
}

// WIDGETS

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const nameWidget = ({axis}) => {
  const onNameChange = (pNewName) => {
    if (axis.getName() != pNewName) {
      axis.setName(pNewName);
    }
  };
  return widgets.stringWidget('Name', 'Friendly name', axis.getName(), onNameChange);
};



/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const typeWidget = ({config, axis}) => {

  const axisTypesToMenuOptions = (pActuatorTypes, pSelectedAxisType) => {
    // Correspondance between key and displayed name
    const lMSInstTypeFormatted = pActuatorTypes.map(function(pActuatorType) {
      return {
        value       : pActuatorType,
        text        : formatAxisType(pActuatorType),
        deprecated  : isAxisTypeDeprecated(pActuatorType)
      };
    });

    const sortedOptions = lMSInstTypeFormatted.sort((a, b) =>  {
      if(a.value === "custom") { return 1; } // always move "custom" to the end of the list
      if(a.text < b.text) { return -1; }
      if(a.text > b.text) { return 1; }
      return 0;
    });
    return sortedOptions
      .filter(pOption => !(pOption.deprecated === true && pOption.value !== pSelectedAxisType)) // do not list the depricated options
      .map(pOption => {
        const isSelected = pSelectedAxisType === pOption.value;
        return selectOption(pOption.value, pOption.text, false, isSelected);
      });
  };

  const onTypeChange = (pValue) => {
    const lNumberOfInstructionsEffected = config.mLibrary.mValidator.instructionsDependingOnActuatorType(axis, pValue).length;
    const lAxisOriginalType =  axis.getType();
    const lResult = axis.setType(pValue);
    if (lNumberOfInstructionsEffected && !lResult) {
      // NOTE: we only fall here if the actuator change is possible in the first place.
      // this is why we need to change the type to see if we have reached maximum number of actuators,
      // We will cancel the change, to allow the user to back out, (in case he quit the page),
      widgets.showConfirmationModal(
        'Change Actuator Type', `This action will result in ${lNumberOfInstructionsEffected} invalid MachineLogic command${lNumberOfInstructionsEffected > 1 ? 's' : ''}.`,
        function() { axis.setType(pValue); },
        function() { axis.setType(lAxisOriginalType); }
      );
    }
    // catch any other reason that prevent the creation of the Axis, display the cause and restore old selection
    if (lResult && lResult.error) {
      widgets.messageBox(lResult.error, 'warning');
    }
  };

  const lAxisTypes = getAxisConfigOptions(config, 'axisType');
  const lSelectedAxisType = axis.getType();

  const lTypeSelect = selectComponent(
    'Type',
    axisTypesToMenuOptions(lAxisTypes, lSelectedAxisType),
    lSelectedAxisType,
    'Type of actuator',
    onTypeChange
  );

  return lTypeSelect.widget;
};


/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const motorSizeWidget = ({axis}) => {

  const motorSizeOptions = axis.getCompatibleMotorSizes().map(pMotorSize => selectOption(pMotorSize,pMotorSize,false));

  return selectComponent(
    'Motor Size',
    motorSizeOptions,
    axis.getMotorSize(),
    'Motor size for all motors',
    pValue => axis.setMotorSize(pValue)
  ).widget;
};


/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const gearboxInstalledWidget = ({axis}) => {
  return view_widgets.checkboxWidget(
    'Gearbox Installed',
    axis.multiplier2gearbox(axis.getMultiplier()),
    pValue => axis.setMultiplier(axis.gearbox2multiplier(pValue))
  );
};


/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const brakeInstalledWidget = ({axis}) => {
  return view_widgets.checkboxWidget(
    'Brake Installed',
    axis.getBrake(),
    pValue => axis.setBrake(pValue)
  );
};

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const customGainWidget = ({axis}) => {
  return widgets.numberWidget(
    'Customize Gain',
    'Gain',
    axis.getGain(),
    axis.getUnits() + "/turn",
    { min: 1, max: 500, step:0.01 },
    pValue => axis.setGain(parseFloat(pValue))
  );
};

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const motorCurrentWidget = ({axis}) => {
  return widgets.numberWidget(
    'Custom Current',
    'Current',
    axis.getCurrent(),
    "A",
    axis.getCompatibleMotorCurrentInfo(),
    pValue => axis.setCurrent(parseFloat(pValue))
  );
};


/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const tuningProfileWidget = ({axis}) => {
  const tuningProfilesToMenuOptions = (pTuningProfiles, pSelectedTuningProfile) => {
    return pTuningProfiles
      .map(pTuningProfile => {
        const isSelected = pSelectedTuningProfile === pTuningProfile;
        return selectOption(pTuningProfile, formatAxisTuningProfile(pTuningProfile), false, isSelected);
      });
  };

  const lSelectedTuningProfile = axis.getTuningProfile();

  return selectComponent(
    'Tuning Profile',
    tuningProfilesToMenuOptions(axis.getCompatibleTuningProfiles(), lSelectedTuningProfile),
    lSelectedTuningProfile,
    'Tuning profile for all motors',
    pValue => axis.setTuningProfile(pValue)
  ).widget;
};

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const homingSpeedWidget = ({axis}) => {
  return widgets.numberWidget(
    'Homing Speed',
    'Homing Speed',
    axis.getHomingSpeed(),
    `${axis.getUnits()}/s`,
    axis.getHomingSpeedInfo(),
    pValue => axis.setHomingSpeed(parseFloat(pValue))
  );
};

/**
 *
 * @param {AxisComponentState} pState
 * @returns {JQuery<HTMLElement>[]}
 */
const  advancedWidgets = (pState) => {
  const {axis, config} = pState;
  const lWidgetComponents = [];
  const lMotorSetting = config.mLibrary.getInstructionSet().config.axis.motorSettings[axis.getMotorSize()];
  let isAdvancedParamCustomized = false;

  if(axis.getType() === 'custom') {
    const lCustomGain = new ReactiveAxisWidgetComponent({
      createElement: customGainWidget,
      state: pState,
      container: $('<div>')
    }).render();
    lWidgetComponents.push(lCustomGain);

    isAdvancedParamCustomized = true;
  }
  const lMotorCurrent = new ReactiveAxisWidgetComponent({
    createElement: motorCurrentWidget,
    state: pState,
    container: $('<div>')
      .addClass('axis_config_advanced_content_container_second_col') // based on the designs, the motor current should be in the 2nd column. Otherwise we could remove this.
      .attr(CYPRESS_DATA_ATTRIBUTE_NAME, CypressDataAttributes.CURRENT_CONFIGURATION_WIDGET)
  }).render();
  lWidgetComponents.push(lMotorCurrent);

  if (axis.getCurrent() !== axis.getDefaultMotorCurrent()) {
    isAdvancedParamCustomized = true;
  }

  if(lMotorSetting.tuningProfile) {
    const lTuningProfile = new ReactiveAxisWidgetComponent({
      createElement: tuningProfileWidget,
      state: pState,
      container: $('<div>')
    }).render();
    lWidgetComponents.push( lTuningProfile);
    if (axis.getTuningProfile() !== axis.getDefaultTuningProfile()) {
      isAdvancedParamCustomized = true;
    }
  }

  const lHomingSpeedWidget = new ReactiveAxisWidgetComponent({
    createElement: homingSpeedWidget,
    state: pState,
    container: $('<div>').attr(CYPRESS_DATA_ATTRIBUTE_NAME, CypressDataAttributes.HOMING_SPEED_WIDGET),
    axisListenerNames: ['onMultiplierChanged']
  }).render();
  lWidgetComponents.push(lHomingSpeedWidget);

  if (axis.getHomingSpeed() !== axis.getDefaultHomingSpeed()) {
    isAdvancedParamCustomized = true;
  }


  //   add mutlidrive here
  return {
    widgetComponents: lWidgetComponents,
    isCustomized: isAdvancedParamCustomized
  };
};



/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>[]}
 */
const createIOSelectWidgetGroup = ({axis, config}) => {
  if(axis.getType() === 'ac_motor_with_vfd') {
    const lOnOffWidget = new ReactiveAxisWidgetComponent({
      createElement: vfdOnOffComponent,
      state: {axis, config},
      container: $('<div>'),
      configListenerNames: ['onIOMapUpdate']
    }).render();
    const lForwarReverseWidget = new ReactiveAxisWidgetComponent({
      createElement: vfdForwardReverseComponent,
      state: {axis, config},
      container: $('<div>'),
      configListenerNames: ['onIOMapUpdate']
    }).render();
    return [ lOnOffWidget, lForwarReverseWidget ];
  } else {
    const lPushWidget = new ReactiveAxisWidgetComponent({
      createElement: pushComponent,
      state: {axis, config},
      container: $('<div>'),
      configListenerNames: ['onIOMapUpdate']
    }).render();
    const lPullWidget = new ReactiveAxisWidgetComponent({
      createElement: pullComponent,
      state: {axis, config},
      container: $('<div>'),
      configListenerNames: ['onIOMapUpdate']
    }).render();

    return [ lPushWidget, lPullWidget ];
  }
};


/**
 * Creates a function to function a config checker button.
 *
 * @param {Navigate} navigate A function to navigate to another page.
 * @returns {(AxisComponentState) => jQuery} A function to create a config checker button.
 */
const createConfigCheckerButtonCreator = (navigate) => ({ axis, config }) => {
  // config checker button must come last
  if (axis.isConfigCheckerCompatible() && config.getController().isAvailable()) {
    const lIsDisabled = config.getConfigManager().mDirty;
    const checkConfigurationButton = $('<button>').addClass('ml_check_config_referal button disabled').css('width', '100%');
    const lIcon = $('<i>').attr('id', 'config_checker_status_icon');

    const configCheckerResult = axis.getConfigCheckerResult();

    if (configCheckerResult === configCheckerResultStates.VALID) {
      lIcon.addClass('icon-check-filled');
      checkConfigurationButton.text('Configuration Validated');
      checkConfigurationButton.append(lIcon);
    }
    else if (configCheckerResult === configCheckerResultStates.INVALID) {
      lIcon.addClass('icon-warning');
      checkConfigurationButton.text('Recheck Configuration');
      checkConfigurationButton.append(lIcon);
    }
    else if (configCheckerResult === configCheckerResultStates.UNCHECKED) {
      checkConfigurationButton.text('Check Configuration');
      checkConfigurationButton.addClass('never_checked');
    }
    else if (configCheckerResult === configCheckerResultStates.UNKNOWN) {
      checkConfigurationButton.text('Loading');
    }

    checkConfigurationButton.toggleClass('disabled', lIsDisabled);
    checkConfigurationButton.attr('disabled', lIsDisabled);

    checkConfigurationButton.append(lIcon);
    checkConfigurationButton.on('click', () => {
      // prevent going to the config-checker page if we have not applied configurations
      if(config.getConfigManager().mDirty) {
        widgets.messageBox('Click "Apply All Configurations" before checking the configuration', 'warning');
      }
      else {
        // TODO GD This code should ultimately live in the pendant. The VSE should not be aware of the pendant routes
        navigate({ pathname: "/check-config", search: `?uuid=${axis.getUUID()}` });
      }
    });

    return checkConfigurationButton;
  }

  return $('<div>').css('display', 'none');
};



export const axisConfigCard = (pNavigate, pConfig, pAxis) => {
  /**
   * @param {AxisComponentState} state
   * @returns {JQuery<HTMLElement> | JQuery<HTMLElement>[]}
   */
  const createAxisWidget = ({ config, axis }) => {
    const allComponentState = {
      config,
      axis
    };

    const lOnClose = () => {

      const lDeleteAxis = () => {
        config.removeAxis(axis);
      };

      const lNumberOfInstructionsEffected = config.mLibrary.mValidator.instructionsUsingActuators(axis).length;
      if (lNumberOfInstructionsEffected > 0) {
        widgets.showConfirmationModal(
          'Delete Actuator',
          `This action will result in ${lNumberOfInstructionsEffected} invalid command${lNumberOfInstructionsEffected > 1 ? 's' : ''}.`,
          lDeleteAxis
        );
      } else {
        lDeleteAxis();
      }
    };

    const lAxisWidget = configCardComponent(axis.getAxisDriveLabel(), lOnClose, true);
    const lMainContent = $('<div>').addClass('axis_config_main_content');
    const lAdvancedButtonContainer = $('<div>').addClass('axis_config_advanced_button_container');
    const lAdvancedContentContainer = $('<div>').addClass('axis_config_advanced_content_container');
    const lIoContentContainer = $('<div>').addClass('axis_config_io_container');

    const lNameWidget = new ReactiveAxisWidgetComponent({
      createElement: nameWidget,
      state: allComponentState,
      container: $('<div>'),
      configListenerNames: ['onAxisNameChanged']
    }).render();

    const lTypeWidget = new ReactiveAxisWidgetComponent({
      createElement: typeWidget,
      state: allComponentState,
      container: $('<div>'),
      axisListenerNames: ['onTypeChanged']
    }).render();

    lMainContent.append(lNameWidget, lTypeWidget);

    // finish handling the first row of the widget
    if(axis.isNonMotorized()) {
      const lControlDeviceWidget = new ReactiveAxisWidgetComponent({
        createElement: controlDeviceComponent,
        state: allComponentState,
        container: $('<div>'),
        configListenerNames: ['onIOMapUpdate']
      }).render();
      lMainContent.append(lControlDeviceWidget);
    }

    if (axis.getMotorCompatibility()) {
      const lMotorSizeWidget = new ReactiveAxisWidgetComponent({
        createElement: motorSizeWidget,
        state: allComponentState,
        container: $('<div>').attr(CYPRESS_DATA_ATTRIBUTE_NAME, CypressDataAttributes.MOTOR_SIZE_SELECTOR_WIDGET),
        axisListenerNames: ['onTypeChanged']
      }).render();
      lMainContent.append(lMotorSizeWidget);
    }

    if(axis.isNonMotorized()) {
      // handles the io widgets required for pneumatics, bag gripper and ac motor
      lIoContentContainer.append(createIOSelectWidgetGroup(allComponentState));
    } else {

      if (axis.getBrakeCompatability() || axis.getGearboxCompatibility()) {
        const lCheckboxGroup = $('<div>').addClass('axis_config_checkbox_group');
        // gearbox installed
        if (axis.getGearboxCompatibility()) {
          const lGearboxInstalledWidget = new ReactiveAxisWidgetComponent({
            createElement: gearboxInstalledWidget,
            state: allComponentState,
            container: $('<div>').attr(CYPRESS_DATA_ATTRIBUTE_NAME, CypressDataAttributes.GEARBOX_CHECKBOX_WIDGET),
            axisListenerNames: ['onTypeChanged']
          }).render();
          lCheckboxGroup.append(lGearboxInstalledWidget);
        }

        // brake installed
        if (axis.getBrakeCompatability()) {
          const lBrakeInstalledWidget = new ReactiveAxisWidgetComponent({
            createElement: brakeInstalledWidget,
            state: allComponentState,
            container: $('<div>').attr(CYPRESS_DATA_ATTRIBUTE_NAME, CypressDataAttributes.BRAKE_CHECKBOX_WIDGET),
            axisListenerNames: ['onTypeChanged']
          }).render();
          lCheckboxGroup.append(lBrakeInstalledWidget);
        }

        lMainContent.append(lCheckboxGroup);
      }

      // advanced widget
      const lText = $('<span>Advanced<span>');
      const lCarat = $('<span>').addClass('fas fa-caret-down');
      const lAdvancedToggle = $(`<button>`).addClass('axis_config_advanced_button');
      lAdvancedToggle.append(lText, lCarat);
      const {isCustomized, widgetComponents} = advancedWidgets(allComponentState);
      lAdvancedContentContainer.append(widgetComponents);
      lAdvancedContentContainer.css('display', 'none');

      const onToggleAdvanced = () => {
        if (lAdvancedContentContainer.css('display') == 'none') {
          lAdvancedContentContainer.css('display', 'grid');
          lCarat.css('transform', 'rotate(180deg)');
        } else {
          lAdvancedContentContainer.css('display', 'none');
          lCarat.css('transform', 'rotate(0deg)');
        }
      };

      lAdvancedToggle.on('click', onToggleAdvanced);
      lAdvancedButtonContainer.append(lAdvancedToggle);

      if (isCustomized) {
        onToggleAdvanced();
      }
    }

    const lConfigCheckerButtonContainer = new ReactiveAxisWidgetComponent({
      createElement: createConfigCheckerButtonCreator(pNavigate),
      state: allComponentState,
      container: $('<div>').css({width: '100%', paddingBottom: '10px'}),
      axisListenerNames: ['onConfigCheckerResultChanged'],
      configManagerListenerNames: ['onSetDirty', 'onConfigDeployed']
    }).render();


    lAxisWidget.updateContents(lMainContent, lAdvancedButtonContainer, lAdvancedContentContainer, lIoContentContainer, lConfigCheckerButtonContainer);

    return lAxisWidget.widget;
  };

  const lAxisWidget = new ReactiveAxisWidgetComponent({
    createElement: createAxisWidget,
    state: {
      config: pConfig,
      axis: pAxis
    },
    container: $('<div>'),
    axisListenerNames: ['onTypeChange']
  }).render();

  const lConfigListener = {
    onAxisRemoved: (pDeletedAxis) => {
      if (pDeletedAxis.mUUID === pAxis.mUUID) {
        lAxisWidget.remove();
      }
    },
    onActuatorRemoved: () => {
      if (pAxis.getActuators().length === 0) {
        pConfig.removeAxis(pAxis);
      }
    }
  };

  pConfig.addListener(lConfigListener);

  lAxisWidget.on('remove', () => {
    pConfig.removeListener(lConfigListener);
  });

  return lAxisWidget;
};
